import type { NavMenuItems } from '@omnipresentgroup/design-system';

import type { NavigateFunction } from './types';

export const createManagerPaths = (
  appUrls: Record<'omniplatform' | 'manager', string>,
) => {
  return {
    HOME_PAGE_PATH: { path: '/', origin: appUrls.omniplatform },
    EMPLOYEES_PATH: { path: '/employees', origin: appUrls.manager },
    CONTRACTORS_PATH: { path: '/contractors', origin: appUrls.manager },
    EXPENSE_LIST_PAGE: { path: '/expenses', origin: appUrls.manager },
    TIME_PAGE: { path: '/time', origin: appUrls.manager },
    TIMESHEETS_PATH: { path: '/timesheets', origin: appUrls.manager },
    ADDITIONAL_PAY_PATH: {
      path: '/additional-pay',
      origin: appUrls.manager,
    },
    SALARY_CHANGES_PATH: { path: '/salary-changes', origin: appUrls.manager },
    MANAGER_INVOICE_LIST_PATH: {
      path: '/invoices',
      origin: appUrls.manager,
    },
    MANAGER_BALANCES_PATH: {
      path: '/balances',
      origin: appUrls.omniplatform,
    },
    MANAGER_PAYROLL_LIST_PATH: {
      path: '/payroll-reports',
      origin: appUrls.omniplatform,
    },
    MANAGER_RESIGNATIONS_PATH: {
      path: '/resignations',
      origin: appUrls.omniplatform,
    },
    MANAGER_TERMINATIONS_PATH: {
      path: '/terminations',
      origin: appUrls.omniplatform,
    },
    CONTRACTOR_INVOICES_PATH: {
      path: '/contractor-invoices',
      origin: appUrls.manager,
    },
    COMPANY_DETAILS_PAGE: { path: '/profile', origin: appUrls.manager },
    LEAVE_SETTINGS_PAGE: { path: '/leave-settings', origin: appUrls.manager },
    DOCUMENT_LIST_PAGE: { path: '/documents', origin: appUrls.manager },
    MANAGER_INTEGRATIONS_PATH: {
      path: '/integrations',
      origin: appUrls.manager,
    },
    MANAGER_BENEFITS_PATH: {
      path: '/benefits',
      origin: appUrls.manager,
    },
    MANAGER_ATLAS_PATH: { path: '/atlas', origin: appUrls.omniplatform },
  };
};

export type ManagerPermissions = {
  isSuperManager: boolean;
  isFinanceManager: boolean;
  isPayrollManager: boolean;
};

export type ManagerFeatureFlags = {
  enablePayrollReportsTab?: boolean;
  enableTimesheetsArbeitszeit?: boolean;
  enableNewIntegration?: boolean;
  enableBenefits?: boolean;
};

const customNavigate = (
  navigate: NavigateFunction,
  { path, origin }: { path: string; origin: string },
) => {
  const targetUrl = `${origin}${path === '/' ? '' : path}`;
  const isInternal = window.location.origin === origin;

  if (isInternal) {
    navigate(path);
  } else {
    window.location.href = targetUrl;
  }
};

export const managerNavItems = ({
  permissions,
  featureFlags,
  navigate,
  appUrls,
}: {
  permissions: ManagerPermissions;
  featureFlags: ManagerFeatureFlags;
  navigate: NavigateFunction;
  appUrls: Record<'omniplatform' | 'manager', string>;
}) => {
  const managerPaths = createManagerPaths(appUrls);

  return [
    {
      name: 'Home',
      icon: 'Home',
      onClick: () => customNavigate(navigate, managerPaths.HOME_PAGE_PATH),
      id: managerPaths.HOME_PAGE_PATH.path,
    },
    {
      name: 'Team',
      icon: 'Users',
      id: '/team',
      items: [
        {
          name: 'People',
          onClick: () => customNavigate(navigate, managerPaths.EMPLOYEES_PATH),
          id: managerPaths.EMPLOYEES_PATH.path,
        },
        ...(permissions.isSuperManager
          ? [
              {
                name: 'Contractors',
                onClick: () =>
                  customNavigate(navigate, managerPaths.CONTRACTORS_PATH),
                id: managerPaths.CONTRACTORS_PATH.path,
              },
            ]
          : []),
        {
          name: 'Expenses',
          onClick: () =>
            customNavigate(navigate, managerPaths.EXPENSE_LIST_PAGE),
          id: managerPaths.EXPENSE_LIST_PAGE.path,
        },
        {
          name: 'Time off',
          onClick: () => customNavigate(navigate, managerPaths.TIME_PAGE),
          id: managerPaths.TIME_PAGE.path,
        },
        ...(featureFlags.enableTimesheetsArbeitszeit
          ? [
              {
                name: 'Timesheets',
                onClick: () =>
                  customNavigate(navigate, managerPaths.TIMESHEETS_PATH),
                id: managerPaths.TIMESHEETS_PATH.path,
              },
            ]
          : []),
        {
          name: 'Additional pay',
          onClick: () =>
            customNavigate(navigate, managerPaths.ADDITIONAL_PAY_PATH),
          id: managerPaths.ADDITIONAL_PAY_PATH.path,
        },
        {
          name: 'Salary changes',
          onClick: () =>
            customNavigate(navigate, managerPaths.SALARY_CHANGES_PATH),
          id: managerPaths.SALARY_CHANGES_PATH.path,
        },
        ...(featureFlags.enableBenefits
          ? [
              {
                name: 'Benefits',
                onClick: () =>
                  customNavigate(navigate, managerPaths.MANAGER_BENEFITS_PATH),
                id: managerPaths.MANAGER_BENEFITS_PATH.path,
              },
            ]
          : []),
        ...(permissions.isSuperManager
          ? [
              {
                name: 'Resignations',
                onClick: () =>
                  customNavigate(
                    navigate,
                    managerPaths.MANAGER_RESIGNATIONS_PATH,
                  ),
                id: managerPaths.MANAGER_RESIGNATIONS_PATH.path,
              },
            ]
          : []),
        {
          name: 'Terminations',
          onClick: () =>
            customNavigate(navigate, managerPaths.MANAGER_TERMINATIONS_PATH),
          id: managerPaths.MANAGER_TERMINATIONS_PATH.path,
        },
      ],
    },
    ...(permissions.isSuperManager ||
    permissions.isFinanceManager ||
    permissions.isPayrollManager
      ? [
          {
            name: 'Billing',
            icon: 'Cash',
            id: '/billing',
            items: [
              ...(permissions.isSuperManager || permissions.isFinanceManager
                ? [
                    {
                      name: 'Invoices',
                      onClick: () =>
                        customNavigate(
                          navigate,
                          managerPaths.MANAGER_INVOICE_LIST_PATH,
                        ),
                      id: managerPaths.MANAGER_INVOICE_LIST_PATH.path,
                    },
                  ]
                : []),
              ...(permissions.isSuperManager || permissions.isFinanceManager
                ? [
                    {
                      name: 'Balances',
                      onClick: () =>
                        customNavigate(
                          navigate,
                          managerPaths.MANAGER_BALANCES_PATH,
                        ),
                      id: managerPaths.MANAGER_BALANCES_PATH.path,
                    },
                  ]
                : []),
              ...((permissions.isSuperManager ||
                permissions.isPayrollManager) &&
              featureFlags.enablePayrollReportsTab
                ? [
                    {
                      name: 'Payroll',
                      onClick: () =>
                        customNavigate(
                          navigate,
                          managerPaths.MANAGER_PAYROLL_LIST_PATH,
                        ),
                      id: managerPaths.MANAGER_PAYROLL_LIST_PATH.path,
                    },
                  ]
                : []),
              ...(permissions.isSuperManager
                ? [
                    {
                      name: 'Contractor invoices',
                      onClick: () =>
                        customNavigate(
                          navigate,
                          managerPaths.CONTRACTOR_INVOICES_PATH,
                        ),
                      id: managerPaths.CONTRACTOR_INVOICES_PATH.path,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      name: 'Company',
      icon: 'Briefcase',
      id: '/company',
      items: [
        {
          name: 'Company details',
          onClick: () =>
            customNavigate(navigate, managerPaths.COMPANY_DETAILS_PAGE),
          id: managerPaths.COMPANY_DETAILS_PAGE.path,
        },
        {
          name: 'Leave settings',
          onClick: () =>
            customNavigate(navigate, managerPaths.LEAVE_SETTINGS_PAGE),
          id: managerPaths.LEAVE_SETTINGS_PAGE.path,
        },
        ...(permissions.isSuperManager
          ? [
              {
                name: 'Documents',
                onClick: () =>
                  customNavigate(navigate, managerPaths.DOCUMENT_LIST_PAGE),
                id: managerPaths.DOCUMENT_LIST_PAGE.path,
              },
            ]
          : []),
        ...(featureFlags.enableNewIntegration
          ? [
              {
                name: 'Integrations',
                onClick: () =>
                  customNavigate(
                    navigate,
                    managerPaths.MANAGER_INTEGRATIONS_PATH,
                  ),
                id: managerPaths.MANAGER_INTEGRATIONS_PATH.path,
              },
            ]
          : []),
      ],
    },
    {
      name: 'Resources',
      icon: 'ClipboardText',
      id: '/resources',
      items: [
        {
          name: 'OmniAtlas',
          onClick: () =>
            customNavigate(navigate, managerPaths.MANAGER_ATLAS_PATH),
          id: managerPaths.MANAGER_ATLAS_PATH.path,
        },
      ],
    },
  ].filter(Boolean) as NavMenuItems;
};
